/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description getSearchUrlFromPathAndFilters
 */

import { PropertyFeatures } from '@zcode/enums'
import { updateUrlParam } from '@zcode/nr/es/shared/utils/urls/updateUrlParam'
import { omap } from '@zcode/nr/es/shared/utils/utils'
import type { Filters } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import deepequal from 'fast-deep-equal'

import { DEFAULT_SORT, FILTER_TYPES, URL_FILTER_QUERY_KEYS } from '../../constants/filterConstants'
import { removeOrFeatureVerticals } from './removeOrFeatureVerticals'

const filterFeatureVerticals: number[] = [
  PropertyFeatures.INCOME_RESTRICTED,
  PropertyFeatures.NO_SECURITY_DEPOSIT,
  PropertyFeatures.SPECIALS,
  PropertyFeatures.UTILITIES_INCLUDED,
]

const isNonEmptyArray = <T>(something?: readonly T[] | null): something is readonly T[] => {
  return !!(something as readonly T[] | undefined)?.length
}

const getFeatureParams = (features: readonly number[] | null) => {
  const featureParams = features?.filter((feature: number) => {
    return !filterFeatureVerticals.includes(feature)
  })
  return isNonEmptyArray(featureParams) ? featureParams.join(',') : ''
}

export const getSearchUrlFromPathAndFilters = ({
  filters,
  isFavoritesView,
  locationUrlQueries,
  omitBox,
  path,
}: {
  filters: Partial<Filters>
  isFavoritesView?: boolean
  locationUrlQueries?: string[]
  omitBox?: boolean
  path?: string
}) => {
  let url = path ?? ''
  url = updateUrlParam(url, 'favorites', isFavoritesView ? true : null)

  if (filters?.orAmenities?.length) {
    url = updateUrlParam(url, 'or-amenities', JSON.stringify(filters.orAmenities))
  }

  if (filters?.orFeatures?.[0]?.length) {
    const paramOrFeatures = removeOrFeatureVerticals(filters.orFeatures[0])
    url = paramOrFeatures.length
      ? updateUrlParam(url, 'or-features', JSON.stringify([paramOrFeatures]))
      : url
  }

  // bathroom
  url = updateUrlParam(url, 'bathrooms', filters.minBathrooms ?? null)
  if (isNonEmptyArray(filters.bathroomsRange)) {
    url = updateUrlParam(url, 'bathrooms-range', filters.bathroomsRange.join(','))
  }
  // propertyTypes
  if (isNonEmptyArray(filters.propertyTypes)) {
    url = updateUrlParam(url, 'property-types', filters.propertyTypes.join(','))
  }
  // guests
  if (isNonEmptyArray(filters.guests)) {
    url = updateUrlParam(url, 'guests', filters.guests.join(','))
  }
  // startDate & endDate
  url = updateUrlParam(
    url,
    URL_FILTER_QUERY_KEYS[FILTER_TYPES.START_DATE],
    filters.startDate ?? null
  )
  url = updateUrlParam(url, URL_FILTER_QUERY_KEYS[FILTER_TYPES.END_DATE], filters.endDate ?? null)
  // sort, only update the url if sort is not the default sort or null
  if (
    isNonEmptyArray(filters.sort) &&
    (filters.maxPricePerBedroomPercentile || !deepequal(filters.sort, DEFAULT_SORT))
  ) {
    if (
      filters.sort.length &&
      (!filters.maxPricePerBedroomPercentile || filters.sort[0] !== 'price')
    ) {
      url = updateUrlParam(url, 'sort', filters.sort ? filters.sort.join(',') : null)
    } else {
      url = updateUrlParam(url, 'sort', null)
    }
  } else {
    url = updateUrlParam(url, 'sort', null)
  }
  // keywords
  url = updateUrlParam(url, 'keywords', filters.keywords ? filters.keywords : null)
  // price-per-bedroom
  url = updateUrlParam(
    url,
    'price-per-bedroom',
    filters.maxPricePerBedroom != null && Number(filters.maxPricePerBedroom) > 0
      ? filters.maxPricePerBedroom
      : null
  )
  // max hours
  url = updateUrlParam(url, 'max-days', isNumber(filters.maxDays) ? filters.maxDays : null)

  // transits
  url = updateUrlParam(
    url,
    'transits',
    filters.transits?.length
      ? omap(filters.transits, (t: string, d: string) => `${t}=${d || ''}`).join(',')
      : null
  )

  // lease-term
  if (filters.shortTerm) {
    url = updateUrlParam(url, 'lease-term', 'short')
  } else {
    url = updateUrlParam(url, 'lease-term', null)
  }

  // sqft
  const minSqft = isNumber(filters.minSquareFeet) ? filters.minSquareFeet : ''
  const maxSqft = isNumber(filters.maxSquareFeet) ? filters.maxSquareFeet : ''

  url = updateUrlParam(url, 'min-square-feet', minSqft || null)
  url = updateUrlParam(url, 'max-square-feet', maxSqft || null)

  // section8
  url = updateUrlParam(url, 'section8', filters.section8 ? true : null)

  // encoded-filters
  url = updateUrlParam(url, 'encoded-filters', filters.encodedFilters ?? null)

  // listing amenities
  if (isNonEmptyArray(filters.listingAmenities)) {
    url = updateUrlParam(url, 'listing-amenities', filters.listingAmenities.join(','))
  }

  // building amenities
  if (isNonEmptyArray(filters.buildingAmenities)) {
    url = updateUrlParam(url, 'building-amenities', filters.buildingAmenities.join(','))
  }

  // features
  if (isNonEmptyArray(filters.features)) {
    // Ignoring income restricted feature since that is the low-income vertical
    const features = filters.features.filter((feature: number) => {
      return !filterFeatureVerticals.includes(feature)
    })
    if (features.length) {
      url = updateUrlParam(url, 'features', features.join(','))
    }
  }

  // put back box
  if (!omitBox && locationUrlQueries && locationUrlQueries.length) {
    url += `${url.includes('?') ? '&' : '?'}${locationUrlQueries.join('&')}`
  }

  // images
  url = updateUrlParam(url, 'images', filters.hasImages ? true : null)

  // student housing
  url = updateUrlParam(url, 'student-housing', filters.studentHousing ? true : null)

  // age restricted
  url = updateUrlParam(url, 'age-restricted', filters.ageRestricted ? true : null)

  // live online tours
  url = updateUrlParam(url, 'live-online-tours', filters.liveOnlineTours ? true : null)

  // virtual tours
  url = updateUrlParam(url, 'has-virtual-tours', filters.hasVirtualTours ? true : null)

  // unavailable listings
  url = updateUrlParam(url, 'hide-no-vacancy', filters.hideNoVacancy ? true : null)

  // available before date
  url = updateUrlParam(url, URL_FILTER_QUERY_KEYS.availableBefore, filters.availableBefore ?? null)

  // waitlist
  url = updateUrlParam(url, URL_FILTER_QUERY_KEYS.waitlisted, filters.waitlisted ?? null)

  // min active units
  url = updateUrlParam(url, URL_FILTER_QUERY_KEYS.minActiveUnits, filters.minActiveUnits ?? null)

  const features = isNonEmptyArray(filters.features) ? filters.features : null
  const featureParams = getFeatureParams(features)
  if (featureParams?.length) {
    url = updateUrlParam(url, 'features', featureParams)
  }

  // qs (multi-queries)
  url = updateUrlParam(url, 'qs', filters.qs ?? null)

  // floorplans
  url = updateUrlParam(url, 'floorplans', filters.floorplans)

  // external rating
  const { externalRating } = filters
  if (externalRating?.min) {
    url = updateUrlParam(url, 'external-rating-min', externalRating?.min)
  }
  if (externalRating?.max) {
    url = updateUrlParam(url, 'external-rating-max', externalRating.max)
  }

  // neighborhoodIds
  const hasNeighborhoodIds = !!(filters.neighborhoodIds && isNonEmptyArray(filters.neighborhoodIds))
  url = updateUrlParam(
    url,
    'neighborhood-ids',
    hasNeighborhoodIds ? filters.neighborhoodIds.join(',') : null
  )

  // zumper expert rating
  const { rating } = filters
  if (rating?.min) {
    url = updateUrlParam(url, 'rating-min', rating.min)
  }
  if (rating?.max) {
    url = updateUrlParam(url, 'rating-max', rating.max)
  }

  return url
}
